import moment from 'moment-timezone';

// The bucket values goserver/apis know; data only, never shown (owner rule: no payment wording on
// session dialogs, so the picker shows a plain time fact instead).
export const TEACHER_CANCEL_BUCKET_OPTIONS = [
  { value: 'OUTSIDE_24H' },
  { value: 'WITHIN_24_1H' },
  { value: 'WITHIN_1H_OR_AFTER' }
];

// Hint bucket at the default 24h/1h cut-offs; seconds-based >= so exact 24h/1h land in the
// earlier bucket, like the engine.
export function derive_cancel_bucket(session_start_epoch, requested_at_local, iana_timezone) {
  if (
    session_start_epoch === undefined ||
    session_start_epoch === null ||
    session_start_epoch === ''
  )
    return '';
  const requested = moment.tz(requested_at_local, iana_timezone);
  if (!requested_at_local || !requested.isValid()) return '';
  const secs = moment(session_start_epoch).diff(requested, 'seconds');
  if (secs >= 24 * 3600) return 'OUTSIDE_24H';
  if (secs >= 3600) return 'WITHIN_24_1H';
  return 'WITHIN_1H_OR_AFTER';
}

// Mirrors apis: a request time more than 2 minutes past now is rejected (clock-skew tolerance).
export const REQUESTED_AT_CLOCK_SKEW_MS = 2 * 60 * 1000;
export const REQUESTED_AT_IN_FUTURE = "Requested at can't be in the future";

export function is_requested_at_in_future(requested_at_local, iana_timezone, now_ms) {
  const iso = requested_at_to_iso(requested_at_local, iana_timezone);
  return !!iso && moment(iso).valueOf() > now_ms + REQUESTED_AT_CLOCK_SKEW_MS;
}

// Expected bucket for a valid request time that isn't in the future ('' otherwise). Data only.
export function preview_cancel_bucket(
  session_start_epoch,
  requested_at_local,
  iana_timezone,
  now_ms
) {
  if (is_requested_at_in_future(requested_at_local, iana_timezone, now_ms)) return '';
  return derive_cancel_bucket(session_start_epoch, requested_at_local, iana_timezone);
}

// "2 h 15 min", "3 d 4 h", "less than a minute": a gap in whole minutes, zero parts left out.
export function gap_text(ms) {
  const mins = Math.floor(Math.abs(ms) / 60000);
  if (mins < 1) return 'less than a minute';
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  return [d ? `${d} d` : '', h ? `${h} h` : '', m ? `${m} min` : ''].filter(Boolean).join(' ');
}

// The plain time fact under the field: when the teacher asked against the class start ('' for an
// empty, invalid or future time, or an unknown start).
export function request_time_fact(session_start_epoch, requested_at_local, iana_timezone, now_ms) {
  const start = Number(session_start_epoch);
  if (session_start_epoch === null || session_start_epoch === '' || !Number.isFinite(start)) {
    return '';
  }
  if (!requested_at_local || is_requested_at_in_future(requested_at_local, iana_timezone, now_ms)) {
    return '';
  }
  const requested = moment.tz(requested_at_local, iana_timezone);
  if (!requested.isValid()) return '';
  const diff = start - requested.valueOf();
  if (Math.abs(diff) < 60000) return 'Teacher asked at the class start time';
  return diff > 0
    ? `Teacher asked ${gap_text(diff)} before the class starts`
    : `Teacher asked ${gap_text(diff)} after the class started`;
}

// The line under the time field: the time fact, or (when this picker shows no time field) where the
// time is entered.
export function cancel_bucket_text({ fact, ownsTimeField, timeFieldNote, factNote }) {
  if (!fact) return ownsTimeField ? '' : `Teacher request time: ${timeFieldNote}`;
  return factNote ? `${fact} (${factNote})` : fact;
}

// datetime-local value read in iana_timezone, as a UTC ISO string ('' when empty or malformed).
export function requested_at_to_iso(requested_at_local, iana_timezone) {
  if (!requested_at_local) return '';
  const m = moment.tz(requested_at_local, iana_timezone);
  return m.isValid() ? m.toISOString() : '';
}

// The page may show another zone; the request time is typed in IST, so the class start is shown in
// IST next to it ('' when the start is unknown).
export function class_start_ist_text(
  session_start_epoch,
  iana_timezone = 'Asia/Kolkata',
  zone_label = 'IST'
) {
  const ms = Number(session_start_epoch);
  if (session_start_epoch === null || session_start_epoch === '' || !Number.isFinite(ms) || ms <= 0)
    return '';
  return `Class starts ${moment(ms).tz(iana_timezone).format('D MMM, h:mm A')} ${zone_label}`;
}
